import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useSubscription } from '@/hooks/useSubscription';

interface FeatureGuardProps {
  featureKey: string;
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Guard component that redirects the user if they don't have access to a specific feature flag.
 */
export function FeatureGuard({ featureKey, children, redirectTo = '/' }: FeatureGuardProps) {
  const { hasFeature, loading } = useSubscription();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!loading && !hasFeature(featureKey)) {
      navigate({ to: redirectTo });
    }
  }, [loading, hasFeature, featureKey, navigate, redirectTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-2xl font-semibold text-gray-400">{t('ui.guards.verifying')}</div>
      </div>
    );
  }

  if (!hasFeature(featureKey)) {
    return null;
  }

  return <>{children}</>;
}
